/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-cycle */
import React, { useCallback, useEffect, useState } from 'react';

import { Alert, CheckboxInput, Icon, Label, Tooltip } from '@meterup/atto';
import type { QuoteRequest, QuoteRequestOnboarding } from '@meterup/connect-api';
import { ErrorWithMessage, put } from '@meterup/connect-api';
import { QuoteRequestOnboarding_CompanyType } from '@meterup/connect-api/src/connectProtos';
import { FormApi } from 'final-form';
import { AsYouType } from 'libphonenumber-js';
import { Field, Form } from 'react-final-form';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router-dom';

import statesJson from '../../../data/states.json';
import { route } from '../../../routes';
import { required, validEmail, validTelephone } from '../../../utils/validation';
import SelectField from '../../Shared/FormFields/SelectField';
import TextInputField from '../../Shared/FormFields/TextInputField';

interface QuotesContractContactInfoFormProps {
  quoteRequest: QuoteRequest;
  quoteRequestOnboarding: QuoteRequestOnboarding | null;
}

function validDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Invalid date' : undefined;
}

type Option = { label: string; value: string };
const stateOptions = Object.entries(statesJson).map(([value, label]) => ({ value, label }));
const companyTypeOptions: Option[] = [
  { label: 'LLC', value: 'llc' },
  { label: 'Corp', value: 'corp' },
  { label: 'Partnership', value: 'partnership' },
];

function isOnsiteContactSameAsSignor(quoteRequestOnboarding: QuoteRequestOnboarding) {
  return quoteRequestOnboarding.signorContactEmail === quoteRequestOnboarding.onsiteContactEmail;
}

function QuotesContractContactInfoForm({
  quoteRequest,
  quoteRequestOnboarding,
}: QuotesContractContactInfoFormProps) {
  const [error, setError] = useState<string | undefined>();
  const [onsiteSameAsSignor, setOnsiteSameAsSignor] = useState(false);

  useEffect(() => {
    if (quoteRequestOnboarding) {
      setOnsiteSameAsSignor(isOnsiteContactSameAsSignor(quoteRequestOnboarding));
    }
  }, [quoteRequestOnboarding]);

  const navigate = useNavigate();

  const handleChangeOnsiteSameAsSignor = (
    checked: boolean,
    form: FormApi<QuoteRequestOnboarding>,
  ) => {
    setOnsiteSameAsSignor(checked);
    if (!checked) {
      form.batch(() => {
        form.change('onsiteContactName', '');
        form.change('onsiteContactEmail', '');
        form.change('onsiteContactPhone', '');
      });
    }
  };

  const onSubmit = useCallback(
    async (values: QuoteRequestOnboarding) => {
      try {
        const request = {
          ...values,
          ...(onsiteSameAsSignor && {
            onsiteContactName: values.signorContactName,
            onsiteContactEmail: values.signorContactEmail,
            onsiteContactPhone: values.signorContactPhone,
          }),
          companyIncorporatedDate: new Date(values.companyIncorporatedDate!).toISOString(),
          contactEmail: undefined, // exists only for 3rd party marketing pixel
        };

        await put(`quote-requests/${quoteRequest.sid}/onboarding`, request);

        TagManager.dataLayer({
          dataLayer: {
            event: 'conversion',
            send_to: 'AW-475626357/K-YMCNyXoJcYEPX25eIB',
            conversionValue: 1.0,
            currency: 'USD',
          },
        });

        navigate(route('completed', { sid: quoteRequest.sid }));
      } catch (e) {
        if (e instanceof ErrorWithMessage) {
          setError(e.message);
        }
      }
    },
    [onsiteSameAsSignor],
  );

  const validateEmail = (value: string) => required(value) || validEmail(value);
  const formatTelephone = (value: string) => new AsYouType('US').input(value || '');
  const validateDate = (value: string) => validDate(value);
  const validateTelephone = (value: string) => required(value) || validTelephone(value);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={
        quoteRequestOnboarding
          ? {
              ...quoteRequestOnboarding,
              contactEmail: quoteRequest.contactEmail,
              companyIncorporatedDate:
                quoteRequestOnboarding.companyIncorporatedDate?.split('T')[0],
            }
          : {
              contactEmail: quoteRequest.contactEmail,
              signorContactName: '',
              signorContactEmail: '',
              signorContactPhone: '',
              onsiteContactName: '',
              onsiteContactEmail: '',
              onsiteContactPhone: '',
              companyTaxid: '',
              companyLegalName: '',
              companyIncorporatedDate: '',
              companyState: '',
              companyType: QuoteRequestOnboarding_CompanyType.unknown,
            }
      }
      render={({ handleSubmit, form }) => (
        <>
          {error && (
            <div className="mt-8">
              <Alert heading={error} variant="negative" />
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            onFocusCapture={() => setError(undefined)}
            id="contactForm"
            className="border border-gray-100 bg-white rounded-lg p-4 mb-12"
          >
            <Field
              name="contactEmail"
              id="contactEmail"
              type="hidden"
              component="input"
              value={quoteRequest.contactEmail}
            />
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2 md:col-span-2 bg-gray-100 rounded p-1 bg-opacity-30">
                Business details
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="companyLegalName">Legal business name*</Label>
                <div className="mt-2">
                  <Field
                    component={TextInputField}
                    aria-label="Company name"
                    size="large"
                    name="companyLegalName"
                    type="text"
                    id="companyLegalName"
                    placeholder="Company"
                    validate={required}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="companyType">Business type*</Label>
                <div className="mt-2">
                  <Field
                    render={(props) => (
                      <SelectField {...props} width="100%" options={companyTypeOptions} />
                    )}
                    aria-label="Business type"
                    size="large"
                    name="companyType"
                    type="text"
                    id="companyType"
                    placeholder="Select business type"
                    validate={required}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="companyTaxid">Tax ID*</Label>
                <div className="mt-2">
                  <Field
                    component={TextInputField}
                    aria-label="Tax ID"
                    size="large"
                    name="companyTaxid"
                    type="text"
                    id="companyTaxid"
                    placeholder="Tax ID"
                    validate={required}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="companyIncorporatedDate">Incorporation date*</Label>
                <div className="mt-2">
                  <Field<string>
                    component={TextInputField}
                    aria-label="Incorporation date"
                    name="companyIncorporatedDate"
                    type="date"
                    size="large"
                    id="companyIncorporatedDate"
                    validate={validateDate}
                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="companyState">Incorporation state*</Label>
                <div className="mt-2">
                  <Field
                    render={(props) => (
                      <SelectField {...props} width="100%" options={stateOptions} />
                    )}
                    aria-label="Incorporation state"
                    size="large"
                    name="companyState"
                    type="text"
                    id="companyState"
                    placeholder="Select incorporation state"
                    validate={required}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-2 bg-gray-100 rounded p-1 bg-opacity-30 gap-2 flex">
                Signer contact
                <Tooltip
                  asChild={false}
                  contents="Contact info of individual authorized to sign the ISP contract."
                >
                  <span className="flex items-center">
                    <Icon icon="information" size={16} />
                  </span>
                </Tooltip>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="signorContactName">First and last name*</Label>
                <div className="mt-2">
                  <Field
                    component={TextInputField}
                    aria-label="First and last name"
                    size="large"
                    icon="user"
                    name="signorContactName"
                    type="text"
                    id="signorContactName"
                    placeholder="First and last name"
                    validate={required}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="signorContactPhone">Phone number*</Label>
                <div className="mt-2">
                  <Field
                    component={TextInputField}
                    aria-label="Phone number"
                    size="large"
                    icon="phone"
                    name="signorContactPhone"
                    type="tel"
                    id="signorContactPhone"
                    placeholder="Phone number"
                    format={formatTelephone}
                    validate={validateTelephone}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="signorContactEmail">Email*</Label>
                <div className="mt-2">
                  <Field
                    component={TextInputField}
                    aria-label="Email"
                    size="large"
                    icon="email"
                    name="signorContactEmail"
                    type="email"
                    id="signorContactEmail"
                    placeholder="Work email address"
                    validate={validateEmail}
                  />
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="signorSameAsContact">
                  Will the signor also be the onsite contact?
                </Label>
                <div className="mt-2">
                  <CheckboxInput
                    checked={onsiteSameAsSignor}
                    onChange={(checked) => handleChangeOnsiteSameAsSignor(checked, form)}
                    aria-label="Will the signor also be the onsite contact?"
                  />
                </div>
              </div>
              {!onsiteSameAsSignor && (
                <>
                  <div className="col-span-2 md:col-span-2 bg-gray-100 rounded p-1 bg-opacity-30 gap-2 flex">
                    Onsite contact
                    <Tooltip
                      asChild={false}
                      contents="Contact information of someone who will be onsite during the installation. This ensures seamless communication and a smooth installation process."
                    >
                      <span className="flex items-center">
                        <Icon icon="information" size={16} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="onsiteContactName">First and last name*</Label>
                    <div className="mt-2">
                      <Field
                        component={TextInputField}
                        aria-label="First and last name"
                        size="large"
                        icon="user"
                        name="onsiteContactName"
                        type="text"
                        placeholder="First and last name"
                        validate={required}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="onsiteContactPhone">Phone number*</Label>
                    <div className="mt-2">
                      <Field
                        component={TextInputField}
                        aria-label="Phone number"
                        size="large"
                        icon="phone"
                        name="onsiteContactPhone"
                        type="tel"
                        id="onsiteContactPhone"
                        placeholder="Phone number"
                        format={formatTelephone}
                        validate={validateTelephone}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="onsiteContactEmail">Email*</Label>
                    <div className="mt-2">
                      <Field
                        component={TextInputField}
                        aria-label="Email"
                        size="large"
                        icon="email"
                        name="onsiteContactEmail"
                        type="email"
                        id="onsiteContactEmail"
                        placeholder="Work email address"
                        validate={validateEmail}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </form>
        </>
      )}
    />
  );
}

export default QuotesContractContactInfoForm;
